import React, { useState, useCallback, useEffect } from 'react';
import { DownloadIcon } from './icons/DownloadIcon';
import { CopyIcon } from './icons/CopyIcon';
import { RefreshCwIcon } from './icons/RefreshCwIcon';
import { CheckIcon } from './icons/CheckIcon';
import PostRedactionSaveModal from './PostRedactionSaveModal';
import {
  AI_PREPARATION_STATUS_READY_FOR_REVIEW,
  PILOT_CONTRIBUTION_NOTE,
  PILOT_FEEDBACK_MAILTO,
  REVIEW_REMINDER,
} from '../config/productCopy';
import { trackPrepareEvent, type AccountSegment } from '../lib/productAnalytics';

interface ResultDisplayProps {
  redactedText: string;
  fileName: string;
  onReset: () => void;
  showWatermark?: boolean;
  onUpgrade?: () => void;
  onOpenSettings?: () => void;
  ocrPageFailures?: number[];
  pageCount?: number;
  isPilot?: boolean;
  accountSegment?: AccountSegment;
  documentCategory?: string;
}

const WATERMARK =
  '\n\n─────────────────────────────────\n🔒 Redact AI – Free Version\nUpgrade for watermark-free prepared exports\n─────────────────────────────────';

const ResultDisplay: React.FC<ResultDisplayProps> = ({
  redactedText,
  fileName,
  onReset,
  showWatermark = false,
  onUpgrade,
  onOpenSettings,
  ocrPageFailures = [],
  pageCount,
  isPilot = false,
  accountSegment = 'unknown',
  documentCategory,
}) => {
  const [copied, setCopied] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const displayText = showWatermark ? redactedText + WATERMARK : redactedText;
  const downloadText = showWatermark ? redactedText + WATERMARK : redactedText;
  const pagesPreserved = pageCount != null && pageCount > 0;

  useEffect(() => {
    setShowSaveModal(true);
  }, []);

  const handleDownload = useCallback(() => {
    setShowSaveModal(true);
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(downloadText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [downloadText]);

  const handleSaveClosedAfterExport = useCallback(() => {
    trackPrepareEvent('document_prepare_downloaded', {
      accountSegment,
      pageCount,
      documentCategory,
      success: true,
    });
  }, [accountSegment, pageCount, documentCategory]);

  return (
    <div className="w-full p-6 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            Document prepared successfully
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Original file: <span className="font-medium">{fileName}</span>
          </p>
          {pagesPreserved && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
              {pageCount} of {pageCount} pages processed successfully.
            </p>
          )}
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
            AI Preparation Status: <span className="font-semibold">{AI_PREPARATION_STATUS_READY_FOR_REVIEW}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleCopy}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition"
          >
            {copied ? <CheckIcon className="w-4 h-4 text-green-500" /> : <CopyIcon className="w-4 h-4" />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
          >
            <DownloadIcon className="w-4 h-4" />
            <span>Download Prepared PDF</span>
          </button>
          <button
            onClick={onReset}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition"
          >
            <RefreshCwIcon className="w-4 h-4" />
            <span>Start Over</span>
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
          Document Preparation Summary
        </h3>
        <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-300">
          <li>✓ PII scan completed</li>
          <li>✓ Redactions applied</li>
          <li>{pagesPreserved ? '✓ Original page count preserved' : '✓ Prepared file generated'}</li>
          {pagesPreserved && <li>✓ Prepared file generated</li>}
        </ul>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Personally identifiable information has been redacted from this AI-ready copy. Review before use.
        </p>
      </div>

      <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
        <p className="text-sm text-amber-800 dark:text-amber-200">{REVIEW_REMINDER}</p>
      </div>

      {ocrPageFailures.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {ocrPageFailures.length} of {pageCount ?? ocrPageFailures.length} pages could not be OCR&apos;d.
            Review carefully — those pages are marked in the text.
          </p>
        </div>
      )}

      {isPilot && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-slate-600 dark:text-slate-300">
          <p>{PILOT_CONTRIBUTION_NOTE}</p>
          <a
            href={PILOT_FEEDBACK_MAILTO}
            className="font-medium text-emerald-700 dark:text-emerald-400 hover:underline whitespace-nowrap"
          >
            Share feedback
          </a>
        </div>
      )}

      <div className="mb-4 flex justify-center md:justify-start">
        <button
          type="button"
          onClick={() => setShowSaveModal(true)}
          className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline"
        >
          Filename & Google Drive save…
        </button>
      </div>

      {showWatermark && onUpgrade && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-amber-600 dark:text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm text-amber-800 dark:text-amber-200">
              Free version includes watermark on prepared exports
            </span>
          </div>
          <button
            onClick={onUpgrade}
            className="text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 underline"
          >
            Remove watermark →
          </button>
        </div>
      )}

      <div className="mt-4 p-4 h-96 overflow-y-auto bg-slate-50 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700">
        <pre className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300 font-sans">{displayText}</pre>
      </div>
      <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-4">
        Document contents are not saved in the Redact AI app database after processing. Your organization remains
        responsible for authorization, vendor terms, and final review before using AI tools or sharing.
      </p>
      <PostRedactionSaveModal
        isOpen={showSaveModal}
        sourceFileName={fileName}
        redactedText={downloadText}
        expectedPageCount={pageCount}
        onClose={() => setShowSaveModal(false)}
        onOpenSettings={onOpenSettings}
        onExportComplete={handleSaveClosedAfterExport}
      />
    </div>
  );
};

export default ResultDisplay;
